import type { NestedFieldValueType } from '@helpcenter/field-types';
import type { NestedOption } from 'view/common/fields/cascading-select';
import type { Option } from '@atlaskit/select';
import { FieldValueLookupError } from '@atlassian/ui-modifications-core/utils/field-value-lookup-error';
import { isNotEmptyString } from '@atlassian/ui-modifications-public-api-utils/is-not-empty-string';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

/**
 *
 * The `children` property supports both `null` and `undefined` to handle different scenarios:
 * - The cascading select component returns `children: undefined` when no child option is present
 * - The cascading select component expects a value object with `children: null` for initialization
 * - This dual type support ensures compatibility across all usage patterns
 */
export interface CascadingSelectValueType {
	primaryValue: NestedOption;
	children: Option<string> | null | undefined;
}

// Types compatible with help-center CascadingSelect component
export type CascadingSelectPublicSetValue = null | CascadingSelectValueType;

export type CascadingSelectInputValue = {
	childId: string | null;
	parentId: string;
} | null;

export type CascadingSelectTransformedValue = {
	parent: {
		id: string;
		value: string;
	};
	child: {
		id: string;
		value: string;
	} | null;
} | null;

const findCascadingSelectLookupValues = (
	lookupData: NestedOption[],
	parentId: string,
	childId: string | null,
) => {
	const foundOption = lookupData.find((currentOption) => currentOption.value === parentId);
	if (!foundOption) {
		throw new FieldValueLookupError();
	}

	const childOption =
		childId !== null && foundOption.children
			? foundOption.children.find((child) => child.value === childId) || null
			: null;

	const value = {
		parent: foundOption,
		children: childOption,
	};

	return value;
};

export const validateCascadingSelectSetValue = (value: CascadingSelectInputValue): boolean => {
	return (
		value === null ||
		(typeof value === 'object' &&
			'parentId' in value &&
			'childId' in value &&
			isNotEmptyString(value.parentId) &&
			(isNotEmptyString(value.childId) || value.childId === null))
	);
};

export const transformSetValueCascadingSelect = (
	value: CascadingSelectInputValue,
	lookupData: NestedOption[],
): CascadingSelectPublicSetValue => {
	if (value === null || value.parentId === null) {
		return null;
	}

	const cascadingSelectValue = findCascadingSelectLookupValues(
		lookupData,
		value.parentId,
		value.childId,
	);

	return {
		primaryValue: cascadingSelectValue.parent,
		children: cascadingSelectValue.children,
	};
};

export const transformGetValueCascadingSelect = (
	cascadingValue: CascadingSelectValueType | null,
): CascadingSelectTransformedValue => {
	if (
		!cascadingValue ||
		(FeatureGates.checkGate('ditto_fix_getvalue_empty_state') && !cascadingValue.primaryValue)
	) {
		return null;
	}

	const { primaryValue, children } = cascadingValue;

	return {
		parent: {
			id: primaryValue.value,
			value: primaryValue.label,
		},
		child: children
			? {
					id: children.value,
					value: children.label,
				}
			: null,
	};
};

interface CascadingSelectUrlParamValue {
	primaryValue: string;
	children?: string;
}

const isCascadingSelectUrlParamValue = (value: unknown): value is CascadingSelectUrlParamValue => {
	if (!value || typeof value !== 'object' || !('primaryValue' in value)) {
		return false;
	}

	const primaryValue = value.primaryValue;
	const children = 'children' in value ? value.children : undefined;

	return (
		typeof primaryValue === 'string' && (children === undefined || typeof children === 'string')
	);
};

/**
 * Converts the string-ID URL-param shape produced by parseCascadingSelectUrlParam into the
 * CascadingSelectValueType shape that UIM's transformGetValueCascadingSelect expects (full
 * NestedOption objects with label + value + children).
 *
 * UIM reads primaryValue.label and primaryValue.value, so passing the raw string shape directly
 * produces { parent: {id: undefined, value: undefined}, child: ... }.
 *
 * The lookup data comes from field.options at the call site. Returns null when the URL-param
 * value is invalid, lookup data is unavailable, or the parent ID cannot be found.
 */
export const transformCascadingSelectUrlParamValue = (
	urlParamValue: unknown,
	lookupData: readonly NestedOption[] | undefined,
): CascadingSelectValueType | null => {
	if (!isCascadingSelectUrlParamValue(urlParamValue) || !lookupData) {
		return null;
	}

	const { primaryValue: parentId, children: childId } = urlParamValue;

	const parentOption = lookupData.find((opt) => opt.value === parentId);
	if (!parentOption) {
		return null;
	}

	const childOption =
		childId !== undefined && parentOption.children
			? (parentOption.children.find((opt) => opt.value === childId) ?? null)
			: null;

	return {
		primaryValue: parentOption,
		children: childOption,
	};
};

export const normalizeCascadingSelectValue = (
	value: NestedFieldValueType | CascadingSelectValueType | null | undefined,
): NestedFieldValueType => {
	if (!value) {
		return { primaryValue: null, children: null };
	}

	// If it's already NestedFieldValueType (string-based primaryValue)
	if (typeof value.primaryValue === 'string' || value.primaryValue === null) {
		return value as NestedFieldValueType;
	}

	// If it's object-based, extract string values
	const childrenValue = (value as CascadingSelectValueType).children?.value;
	return {
		primaryValue: value.primaryValue?.value || null,
		children: childrenValue || null,
	};
};

export const transformValueToBasicCascadingSelect = (
	value: CascadingSelectPublicSetValue,
): NestedFieldValueType => {
	if (!value) {
		return { primaryValue: null, children: null };
	}

	return {
		primaryValue: value.primaryValue?.value || null,
		children: value.children?.value || null,
	};
};
